import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../config/supabase';

export type StockLearnStatus = 'learnt' | 'guessed';

export interface StockHistoryEntry {
  gameDate: string;
  stockSymbol: string;
  companyName: string;
  won: boolean;
  analysisViewed: boolean;
  status: StockLearnStatus;
  logoUrl: string | null;
}

interface UseStockHistoryReturn {
  stocks: StockHistoryEntry[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function deriveStatus(won: boolean, analysisViewed: boolean): StockLearnStatus {
  if (won && analysisViewed) return 'learnt';
  return 'guessed';
}

const STATUS_ORDER: Record<StockLearnStatus, number> = {
  learnt: 0,
  guessed: 1,
};

export const useStockHistory = (enabled: boolean = true): UseStockHistoryReturn => {
  const { user } = useAuth();
  const [stocks, setStocks] = useState<StockHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!enabled || !user?.id) {
      setStocks([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

      // User auth only: never send the admin API key from the browser
      const headers: HeadersInit = { 'Content-Type': 'application/json' };

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(
        `${apiBaseUrl}/api/users/${user.id}/progress`,
        { headers }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch stock history: ${response.status}`);
      }

      const json = await response.json();
      if (!json.success) {
        throw new Error('Invalid response from stock history API');
      }

      const entries: StockHistoryEntry[] = (json.data as Array<{
        game_date: string;
        stock_symbol: string;
        company_name: string;
        won: boolean;
        analysis_viewed: boolean;
        logo_url?: string | null;
      }>)
        .filter((row) => row.won)
        .map((row) => ({
          gameDate: row.game_date,
          stockSymbol: row.stock_symbol,
          companyName: row.company_name,
          won: row.won,
          analysisViewed: row.analysis_viewed,
          status: deriveStatus(row.won, row.analysis_viewed),
          logoUrl: row.logo_url ?? null,
        }));

      entries.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
      setStocks(entries);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load stock history';
      setError(msg);
      setStocks([]);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, user?.id]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return { stocks, isLoading, error, refetch: fetchHistory };
};
